import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import "./ChatBoxV2.css";
import "./RoomPanelShared.css";
import { Button, cn, toast } from "../../../styles/facemeet-ui";
import { logger } from "../../../log/logger";
import { IconSelect, IconTrash, IconMessage, IconX, IconSend} from "./panel_Icons_shared";

export function ChatBox({
  room,
  roomId,
  username,
  userId,
  participants = [],
  userMap = {},
  personalUnreadMap = {},
  setPersonalUnreadMap,
  role = "",
  participantRoles = {},
  unreadGroupExternal = 0,
  unreadPersonalExternal = 0,
  groupChatDisabled = false,
  groupChatAllowed = true,
  bannedFromChat = new Set(),
  onChatOpen = () => {},
  onChatClose = () => {},
}) {
  const API_URL = process.env.REACT_APP_SERVER_URL;
  const inputId = useId();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [chatType, setChatType] = useState("group");
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedMsgIds, setSelectedMsgIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isOpenRef = useRef(true);
  const chatTypeRef = useRef("group");

  const [isPrivateUnlocked, setIsPrivateUnlocked] = useState(false);
  const personalMsgCacheRef = useRef({});

  /* ── Derived values ───────────────────────────────────────────── */
  const hostIdentity = Object.entries(participantRoles).find(([, r]) => r === "host")?.[0];
  const hostParticipant = hostIdentity ? participants.find((p) => p.identity === hostIdentity) : null;
  const othersInRoom =
    role === "host"
      ? participants.filter((p) => p.identity !== userId)
      : hostParticipant ? [hostParticipant] : [];

  const groupBadge = unreadGroupExternal;
  const personalBadge = unreadPersonalExternal;

  /* ── Ref sync ─────────────────────────────────────────────────── */
  useEffect(() => { chatTypeRef.current = chatType; }, [chatType]);

  /* ── Auto-select host for non-host participants ────────────────── */
  useEffect(() => {
    if (role !== "host" && hostParticipant) {
      setSelectedParticipant(hostParticipant.identity);
    }
  }, [role, hostParticipant]);

  /* ── Load helpers ─────────────────────────────────────────────── */
  const loadGroupChat = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/v2/chat-history/${roomId}`);
      const data = await res.json();
      setMessages(data.filter((m) => m.chatType === "group"));
    } catch (err) {
      logger.error(`Group chat load failed | room=${roomId}`, err);
    }
  }, [API_URL, roomId]);

  const loadPersonalChat = useCallback(async (otherId) => {
    if (!otherId) return;
    try {
      const res = await fetch(`${API_URL}/v2/personal-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId, senderId: userId, otherUserId: otherId }),
      });
      const data = await res.json();
      const cached = personalMsgCacheRef.current[otherId] || [];

      const dbIds = new Set(data.map((m) => m._id).filter(Boolean));
      const dbSignatures = new Set(data.map((m) => `${m.senderId}||${m.message}`));

      const getTs = (ts) => {
        if (!ts) return 0;
        if (typeof ts === "number") return ts;
        return new Date(ts).getTime() || 0;
      };

      const merged = [
        ...data,
        ...cached.filter((m) => {
          if (m._id && dbIds.has(m._id)) return false;
          if (dbSignatures.has(`${m.senderId}||${m.message}`)) return false;
          return true;
        }),
      ].sort((a, b) => {
        const ta = getTs(a.timestamp);
        const tb = getTs(b.timestamp);
        if (ta !== tb) return ta - tb;
        if (a._id && !b._id) return -1;
        if (!a._id && b._id) return 1;
        return (a._id || a.senderId + ta).toString().localeCompare((b._id || b.senderId + tb).toString());
      });

      setMessages(merged);

      if (role !== "host" && hostIdentity) {
        const hostHasMessaged = merged.some(
          (m) => m.chatType === "personal" && m.senderId === hostIdentity && m.recipientId === userId,
        );
        if (hostHasMessaged) setIsPrivateUnlocked(true);
      }
    } catch (err) {
      const cached = personalMsgCacheRef.current[otherId] || [];
      if (cached.length) setMessages([...cached]);
      logger.error(`Personal chat load failed`, err);
    }
  }, [API_URL, roomId, userId, role, hostIdentity]);

  /* ── Realtime incoming messages ───────────────────────────────── */
  useEffect(() => {
    const handler = (event) => {
      const msg = event.detail;
      if (!msg || msg.senderId === userId) return;

      const inGroupTab = isOpenRef.current && chatTypeRef.current === "group";
      const inPersonalTab = isOpenRef.current && chatTypeRef.current === "personal";

      if (msg.chatType === "personal" && role !== "host" && msg.senderId === hostIdentity && msg.recipientId === userId) {
        setIsPrivateUnlocked(true);
      }

      if (msg.chatType === "personal") {
        const partnerId = msg.senderId === userId ? msg.recipientId : msg.senderId;
        if (partnerId) {
          const existing = personalMsgCacheRef.current[partnerId] || [];
          const alreadyCached = existing.some((m) => m.timestamp === msg.timestamp && m.senderId === msg.senderId);
          if (!alreadyCached) {
            personalMsgCacheRef.current = {
              ...personalMsgCacheRef.current,
              [partnerId]: [...existing, msg],
            };
          }
        }
      }

      if (msg.chatType === "group" && inGroupTab) setMessages((prev) => [...prev, msg]);
      if (
        msg.chatType === "personal" && inPersonalTab && selectedParticipant &&
        ((msg.senderId === userId && msg.recipientId === selectedParticipant) ||
          (msg.senderId === selectedParticipant && msg.recipientId === userId))
      ) {
        setMessages((prev) => [...prev, msg]);
      }
    };

    window.addEventListener("chat-message", handler);
    return () => window.removeEventListener("chat-message", handler);
  }, [chatType, selectedParticipant, userId, hostIdentity, role]);

  /* ── Realtime tombstone for deletes ───────────────────────────── */
  useEffect(() => {
    const handler = (event) => {
      const { messageIds } = event.detail || {};
      if (!Array.isArray(messageIds) || messageIds.length === 0) return;
      const deletedSet = new Set(messageIds);
      setMessages((prev) => prev.map((m) => (deletedSet.has(m._id) ? { ...m, deleted: true } : m)));
    };
    window.addEventListener("message-deleted", handler);
    return () => window.removeEventListener("message-deleted", handler);
  }, []);

  /* ── Reload on tab / participant change ───────────────────────── */
  useEffect(() => {
    setMessages([]);
    setSelectMode(false);
    setSelectedMsgIds(new Set());
    if (chatType === "group") loadGroupChat();
    else if (chatType === "personal" && selectedParticipant) loadPersonalChat(selectedParticipant);
  }, [roomId, chatType, selectedParticipant, loadGroupChat, loadPersonalChat]);

  /* ── Initial load on mount ────────────────────────────────────── */
  useEffect(() => {
    setMessages([]);
    if (chatType === "group") loadGroupChat();
    else if (chatType === "personal" && selectedParticipant) loadPersonalChat(selectedParticipant);
    onChatOpen(chatType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Auto-scroll on new message ───────────────────────────────── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ── Unlock check ─────────────────────────────────────────────── */
  useEffect(() => {
    if (chatType === "personal" && role !== "host" && hostIdentity) {
      const hasHostMessage = messages.some(
        (m) => m.chatType === "personal" && m.recipientId === userId && m.senderId === hostIdentity,
      );
      if (hasHostMessage) setIsPrivateUnlocked(true);
    }
  }, [messages, chatType, role, userId, hostIdentity]);

  /* ── Tab change ───────────────────────────────────────────────── */
  const handleTabChange = (type) => {
    if (type === chatType) return;
    setChatType(type);
    if (role === "host") setSelectedParticipant(null);
    onChatOpen(type);
  };

  const closeBox = () => {
    setSelectMode(false);
    setSelectedMsgIds(new Set());
    onChatClose();
  };

  /* ── Send message ─────────────────────────────────────────────── */
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || isSending) return;
    if (chatType === "group" && groupChatDisabled && !groupChatAllowed) return;

    const effectiveParticipant = role === "host" ? selectedParticipant : hostIdentity;
    if (chatType === "personal" && !effectiveParticipant) return;

    const text = newMessage.trim();
    const recipientId =
      chatType === "personal"
        ? role === "host" ? selectedParticipant : hostIdentity
        : null;
    const timestamp = Date.now();

    const payload = {
      type: "chat",
      chatType,
      senderId: userId,
      senderName: username,
      message: text,
      recipientId,
      timestamp,
    };

    setNewMessage("");
    setIsSending(true);

    try {
      const saveRes = await fetch(`${API_URL}/v2/send-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId, senderId: userId, senderName: username,
          message: text, chatType, recipientId,
        }),
      });
      const savedMsg = saveRes.ok ? await saveRes.json().catch(() => null) : null;
      const finalPayload = savedMsg?._id ? { ...payload, _id: savedMsg._id } : payload;

      if (room) {
        if (!savedMsg?._id) {
          logger.warn(`Chat broadcast without _id — receivers won't be able to select/delete until refetch`);
        }
        room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(finalPayload)),
          { reliable: true },
        );
      }

      setMessages((prev) => [...prev, finalPayload]);

      if (chatType === "personal" && recipientId) {
        const existing = personalMsgCacheRef.current[recipientId] || [];
        personalMsgCacheRef.current = {
          ...personalMsgCacheRef.current,
          [recipientId]: [...existing, finalPayload],
        };
      }
    } catch (err) {
      logger.error("Send message failed", err);
      setNewMessage(text);
      toast.error("Couldn't send message — try again");
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  /* ── Host: select + delete ────────────────────────────────────── */
  const toggleSelectMsg = (id) => {
    if (!id) return;
    setSelectedMsgIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedMsgIds.size === 0 || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`${API_URL}/v2/delete-messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId, requesterId: userId,
          messageIds: [...selectedMsgIds],
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const deletedIds = new Set(selectedMsgIds);
      setMessages((prev) => prev.map((m) => (deletedIds.has(m._id) ? { ...m, deleted: true } : m)));

      if (room) {
        room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify({
            type: "message-deleted",
            messageIds: [...deletedIds],
          })),
          { reliable: true },
        );
      }
      setSelectedMsgIds(new Set());
      toast.success(`Deleted ${deletedIds.size} message${deletedIds.size === 1 ? "" : "s"}`);
    } catch (err) {
      logger.error("Delete messages failed", err);
      toast.error("Couldn't delete messages");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const date = typeof ts === "number" ? new Date(ts) : new Date(ts);
    return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  /* ── Computed visible + selectable ────────────────────────────── */
  const visibleMessages = messages.filter((msg) => {
    if (chatType === "group") return msg.chatType === "group";
    if (chatType === "personal" && selectedParticipant) {
      return (
        msg.chatType === "personal" &&
        ((msg.senderId === userId && msg.recipientId === selectedParticipant) ||
          (msg.senderId === selectedParticipant && msg.recipientId === userId))
      );
    }
    return false;
  });

  const selectableIds = visibleMessages.filter((m) => m._id && !m.deleted).map((m) => m._id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedMsgIds.has(id));
  const toggleSelectAll = () => {
    if (selectableIds.length === 0) return;
    if (allSelected) setSelectedMsgIds(new Set());
    else setSelectedMsgIds(new Set(selectableIds));
  };

  /* ── Input state ──────────────────────────────────────────────── */
  const inputDisabled =
    (chatType === "group" && groupChatDisabled && !groupChatAllowed) ||
    (chatType === "personal" &&
      (!selectedParticipant || (role !== "host" && !isPrivateUnlocked)));

  const sendDisabled = inputDisabled || !newMessage.trim() || isSending;

  const inputPlaceholder =
    chatType === "group" && groupChatDisabled && !groupChatAllowed
      ? "Group chat is disabled by the host…"
      : role !== "host" && chatType === "personal" && !isPrivateUnlocked
        ? "Waiting for host to start chat..."
        : role === "host" && chatType === "personal" && !selectedParticipant
          ? "Select a participant…"
          : "Message…";

  /* ── Auto-resize textarea ─────────────────────────────────────── */
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="fm-rpanel">
      {/* HEADER */}
      <div className="fm-rpanel__header">
        <div className="fm-rpanel__title-wrap">
          <h3 className="fm-rpanel__title">
            {chatType === "group" ? "Group chat" : "Private chat"}
          </h3>
        </div>
        <div className="fm-rpanel__actions">
          {role === "host" && !selectMode && selectableIds.length > 0 && (
            <button
              className="fm-rpanel__icon-btn"
              onClick={() => setSelectMode(true)}
              title="Select messages to delete"
              aria-label="Select messages to delete"
            >
              <IconSelect size={18} />
            </button>
          )}
          <button
            className="fm-rpanel__icon-btn fm-rpanel__icon-btn--danger"
            onClick={closeBox}
            aria-label="Close chat"
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="fm-chat-tabs">
        <div className="fm-chat-tabs__row" role="tablist" aria-label="Chat type">
          <button
            type="button"
            role="tab"
            aria-selected={chatType === "group"}
            className={cn("fm-chat-tab", chatType === "group" && "fm-chat-tab--active")}
            onClick={() => handleTabChange("group")}
          >
            Group
            {groupBadge > 0 && chatType !== "group" && (
              <span className="fm-chat-tab__dot" aria-label={`${groupBadge} unread`}>
                {groupBadge > 9 ? "9+" : groupBadge}
              </span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={chatType === "personal"}
            className={cn("fm-chat-tab", chatType === "personal" && "fm-chat-tab--active")}
            onClick={() => handleTabChange("personal")}
          >
            Personal
            {personalBadge > 0 && chatType !== "personal" && (
              <span className="fm-chat-tab__dot" aria-label={`${personalBadge} unread`}>
                {personalBadge > 9 ? "9+" : personalBadge}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* HOST SELECT TOOLBAR */}
      {role === "host" && selectMode && (
        <div className="fm-chat-host-toolbar">
          <label className="fm-chat-host-toolbar__left">
            <input
              type="checkbox"
              className="fm-check-input"
              checked={allSelected}
              onChange={toggleSelectAll}
            />
            <span>Select all</span>
          </label>
          <div className="fm-chat-host-toolbar__right">
            {selectedMsgIds.size > 0 && (
              <Button
                variant="danger"
                size="sm"
                loading={isDeleting}
                onClick={handleDeleteSelected}
                leadingIcon={<IconTrash size={14} />}
              >
                Delete ({selectedMsgIds.size})
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectMode(false); setSelectedMsgIds(new Set()); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* PRIVATE LOCKED NOTICE */}
      {chatType === "personal" && role !== "host" && !isPrivateUnlocked && (
        <div className="fm-chat-notice">🔒 Private chat is controlled by the host</div>
      )}

      {/* GROUP CHAT DISABLED NOTICE */}
      {chatType === "group" && groupChatDisabled && !groupChatAllowed && (
        <div className="fm-chat-notice fm-chat-notice--warn">
          🚫 Group chat has been disabled by the host
        </div>
      )}

      {/* PARTICIPANT SELECTOR (host only) */}
      {chatType === "personal" && role === "host" && (
        <div className="fm-chat-participants">
          {othersInRoom.length === 0 ? (
            <p style={{ width: "100%", textAlign: "center", fontSize: "var(--text-xs)", color: "var(--text-faint)", padding: "var(--space-2) 0" }}>
              No other participants yet
            </p>
          ) : (
            othersInRoom.map((p) => {
              const unread = personalUnreadMap?.[p.identity] || 0;
              const active = selectedParticipant === p.identity;
              return (
                <button
                  key={p.identity}
                  className={cn("fm-chat-chip", active && "fm-chat-chip--active")}
                  onClick={() => {
                    setSelectedParticipant(p.identity);
                    if (setPersonalUnreadMap) {
                      setPersonalUnreadMap((prev) => ({ ...prev, [p.identity]: 0 }));
                    }
                  }}
                >
                  {userMap[p.identity] || p.identity}
                  {unread > 0 && !active && (
                    <span className="fm-chat-chip__badge">{unread > 9 ? "9+" : unread}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}

      {/* MESSAGES */}
      <div className="fm-chat-messages">
        {visibleMessages.length === 0 ? (
          <div className="fm-chat-empty">
            <div className="fm-chat-empty__icon"><IconMessage size={22} /></div>
            <p>No messages yet</p>
            <p style={{ fontSize: "var(--text-xs)", color: "var(--text-faint)" }}>
              Say hi to get the conversation started.
            </p>
          </div>
        ) : (
          visibleMessages.map((msg, i) => {
            const msgId = msg._id || `${msg.senderId}-${msg.timestamp}-${i}`;
            const isOwn = msg.senderId === userId;
            const isSelectable = !!msg._id && !msg.deleted;
            const isSelected = isSelectable && selectedMsgIds.has(msgId);

            return (
              <div
                key={msgId}
                className={cn(
                  "fm-msg",
                  isOwn && "fm-msg--own",
                  isSelected && "fm-msg--selected",
                  msg.deleted && "fm-msg--deleted",
                )}
                onClick={() => {
                  if (role === "host" && selectMode && isSelectable) toggleSelectMsg(msgId);
                }}
              >
                {role === "host" && selectMode && (
                  <input
                    type="checkbox"
                    className="fm-check-input fm-msg__check"
                    checked={!!isSelected}
                    disabled={!isSelectable}
                    onChange={() => toggleSelectMsg(msgId)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Select message"
                  />
                )}

                <div className="fm-msg__body">
                  {msg.deleted ? (
                    <div className="fm-msg__deleted">
                      <IconTrash size={12} />
                      <span>Message deleted by the host</span>
                    </div>
                  ) : (
                    <>
                      <div className="fm-msg__meta">
                        <span className="fm-msg__sender">
                          {isOwn ? "You" : (msg.senderName || userMap[msg.senderId] || msg.senderId)}
                        </span>
                        <span className="fm-msg__time">{formatTime(msg.timestamp)}</span>
                      </div>
                      <div className="fm-msg__bubble">{msg.message}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* INPUT */}
      <form onSubmit={handleSendMessage} className="fm-chat-form">
        <label htmlFor={inputId} className="fm-sr-only">Message input</label>
        <textarea
          id={inputId}
          ref={inputRef}
          value={newMessage}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={inputPlaceholder}
          className="fm-chat-input"
          rows={1}
          disabled={inputDisabled}
          aria-label="Type a message"
        />
        <button
          type="submit"
          className="fm-chat-send"
          disabled={sendDisabled}
          aria-label="Send message"
          title="Send (Enter)"
        >
          <IconSend size={16} />
        </button>
      </form>
    </div>
  );
}

export default ChatBox;